import { JsonSchema, UISchemaElement } from "@jsonforms/core";
import {
    FormTemplateSchema,
    FormTemplateDto,
} from "../../../models/formTemplate/formTemplateDto";
import { EditorField, FormEditorState, FormFieldType } from "./types";

let fieldCounter = 0;

export const generateFieldId = (): string => {
    fieldCounter++;
    return `field_${Date.now()}_${fieldCounter}`;
};

export const validateFields = (
    fields: EditorField[]
): { fieldId: string; error: string }[] => {
    const errors: { fieldId: string; error: string }[] = [];
    for (const field of fields) {
        if (!field.label.trim()) {
            errors.push({ fieldId: field.id, error: "emptyLabel" });
        }
        if (field.type === "dropdown" || field.type === "approveReject") {
            const opts = field.options ?? [];
            if (opts.length < 2) {
                errors.push({ fieldId: field.id, error: "tooFewOptions" });
            } else if (opts.some((o) => !o.trim())) {
                errors.push({ fieldId: field.id, error: "emptyOption" });
            }
        }
        if (
            field.type === "number" &&
            field.min != null &&
            field.max != null &&
            field.min > field.max
        ) {
            errors.push({ fieldId: field.id, error: "minGreaterThanMax" });
        }
    }
    return errors;
};

export const buildSchema = (state: FormEditorState): FormTemplateSchema => {
    const properties: Record<string, JsonSchema> = {};
    const required: string[] = [];
    const elements: UISchemaElement[] = [];

    for (const field of state.fields) {
        const prop: JsonSchema = {};

        switch (field.type) {
            case "approveReject":
                prop.type = "string";
                prop.title = field.label;
                if (field.options && field.options.length > 0) {
                    const unique = [...new Set(field.options.filter((o) => o))];
                    if (unique.length > 0) {
                        prop.enum = unique;
                    }
                }
                break;
            case "freeText":
                prop.type = "string";
                prop.title = field.label;
                if (field.maxLength != null) {
                    prop.maxLength = field.maxLength;
                }
                break;
            case "multiLineText":
                prop.type = "string";
                prop.title = field.label;
                if (field.maxLength != null) {
                    prop.maxLength = field.maxLength;
                }
                break;
            case "number":
                prop.type = "number";
                prop.title = field.label;
                if (field.min != null) {
                    prop.minimum = field.min;
                }
                if (field.max != null) {
                    prop.maximum = field.max;
                }
                break;
            case "checkbox":
                prop.type = "boolean";
                prop.title = field.label;
                break;
            case "dropdown":
                prop.type = "string";
                prop.title = field.label;
                if (field.options && field.options.length > 0) {
                    const unique = [...new Set(field.options.filter((o) => o))];
                    if (unique.length > 0) {
                        prop.enum = unique;
                    }
                }
                break;
            case "attachment":
                prop.type = "array";
                prop.format = "attachment";
                prop.title = field.label;
                prop.items = { type: "object" };
                if (field.maxFiles != null) {
                    prop.maxItems = field.maxFiles;
                }
                break;
        }

        if (field.description) {
            prop.description = field.description;
        }

        properties[field.id] = prop;

        if (field.required) {
            required.push(field.id);
        }

        const uiElement: Record<string, unknown> = {
            type: "Control",
            scope: `#/properties/${field.id}`,
        };

        if (field.type === "approveReject") {
            uiElement.options = { format: "radio" };
        }

        if (field.type === "multiLineText") {
            uiElement.options = { multi: true };
        }

        if (field.type === "attachment") {
            uiElement.options = {
                acceptedFileTypes: field.acceptedFileTypes,
                maxFileSize: (field.maxFileSize ?? 10) * 1024 * 1024,
            };
        }

        elements.push(uiElement as unknown as UISchemaElement);
    }

    const schema: JsonSchema = {
        type: "object",
        properties,
    };

    if (required.length > 0) {
        schema.required = required;
    }

    const uiSchema: UISchemaElement = {
        type: "VerticalLayout",
        elements,
    } as unknown as UISchemaElement;

    return {
        schema,
        uiSchema,
    };
};

const inferFieldType = (
    prop: JsonSchema,
    uiElement?: Record<string, unknown>
): FormFieldType => {
    if (prop.format === "attachment") {
        return "attachment";
    }

    if (prop.type === "boolean") {
        return "checkbox";
    }

    if (prop.type === "number" || prop.type === "integer") {
        return "number";
    }

    if (prop.type === "string" && Array.isArray(prop.enum)) {
        const hasRadioFormat =
            uiElement?.options &&
            (uiElement.options as Record<string, unknown>).format === "radio";

        if (hasRadioFormat) {
            return "approveReject";
        }

        return "dropdown";
    }

    if (prop.type === "string") {
        const hasMultiOption =
            uiElement?.options &&
            (uiElement.options as Record<string, unknown>).multi === true;

        if (hasMultiOption) {
            return "multiLineText";
        }
    }

    return "freeText";
};

export const parseSchemaToFields = (
    template: FormTemplateDto
): FormEditorState => {
    const { name } = template;
    const { schema, uiSchema } = template.jsonSchema;
    const fields: EditorField[] = [];
    const properties = (schema.properties ?? {}) as Record<string, JsonSchema>;
    const required: string[] = schema.required ?? [];
    const elements = ((uiSchema as Record<string, unknown>).elements ??
        []) as Record<string, unknown>[];

    const uiElementMap = new Map<string, Record<string, unknown>>();
    for (const el of elements) {
        const scope = el.scope as string | undefined;
        if (scope) {
            const key = scope.replace("#/properties/", "");
            uiElementMap.set(key, el);
        }
    }

    const orderedKeys: string[] = [];
    for (const el of elements) {
        const scope = el.scope as string | undefined;
        if (scope) {
            const key = scope.replace("#/properties/", "");
            if (properties[key]) {
                orderedKeys.push(key);
            }
        }
    }

    for (const key of Object.keys(properties)) {
        if (!orderedKeys.includes(key)) {
            orderedKeys.push(key);
        }
    }

    for (const key of orderedKeys) {
        const prop = properties[key];
        if (!prop) continue;

        const uiElement = uiElementMap.get(key);
        const type = inferFieldType(prop, uiElement);

        const field: EditorField = {
            id: key,
            type,
            label: prop.title ?? key,
            required: required.includes(key),
        };

        if (prop.description) {
            field.description = prop.description;
        }

        if (
            (type === "dropdown" || type === "approveReject") &&
            Array.isArray(prop.enum)
        ) {
            field.options = prop.enum as string[];
        }

        if (prop.minimum != null) {
            field.min = prop.minimum as number;
        }
        if (prop.maximum != null) {
            field.max = prop.maximum as number;
        }
        if (prop.maxLength != null) {
            field.maxLength = prop.maxLength as number;
        }

        if (type === "attachment") {
            if (prop.maxItems != null) {
                field.maxFiles = prop.maxItems;
            }
            const opts = uiElement?.options as
                | Record<string, unknown>
                | undefined;
            if (opts?.maxFileSize) {
                field.maxFileSize =
                    (opts.maxFileSize as number) / (1024 * 1024);
            }
            if (Array.isArray(opts?.acceptedFileTypes)) {
                field.acceptedFileTypes = opts.acceptedFileTypes as string[];
            }
        }

        fields.push(field);
    }

    return { name, fields };
};
